import React, { useMemo, useRef, useCallback, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { useRatedMovies } from '../../hooks/useRatedMovies';
import { NAMEPSACE, CAN_RATE } from '../Globals';
import './movietable.css';

export default function MovieTable({ onClose, getSpecificMovie, filters, searchQuery }) {
    const containerRef = useRef(null);
    const [ratingOverrides, setRatingOverrides] = useState({});
    const [savingByImdbId, setSavingByImdbId] = useState({});
    const {
        rows,
        hasMore,
        isLoading,
        orderBy,
        orderDir,
        setSorting,
        loadMore,
    } = useRatedMovies(filters, searchQuery);

    const columns = useMemo(() => ([
        { key: 'title', label: 'TITLE' },
        { key: 'orig_language', label: 'LANGUAGE' },
        { key: 'released', label: 'RELEASED' },
        { key: 'runtime', label: 'RUNTIME' },
        { key: 'imdb_rating', label: 'IMDB' },
        { key: 'rotten_tomatoes_rating', label: 'ROTTEN' },
        ...(CAN_RATE ? [{ key: 'kino_rating', label: 'RATING' }] : []),
    ]), []);

    const onHeaderClick = useCallback((key) => {
        if (isLoading) return;
        if (containerRef.current) {
            try { containerRef.current.scrollTop = 0; } catch (_) {}
        }
        if (orderBy === key) {
            setSorting(key, orderDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSorting(key, 'asc');
        }
    }, [orderBy, orderDir, setSorting, isLoading]);

    const onScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (!el) return;
        const threshold = 150; // px from bottom
        if (hasMore && !isLoading && el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
            loadMore();
        }
    }, [hasMore, isLoading, loadMore]);

    const saveRating = useCallback(async (imdbId, rating) => {
        const payload = { imdb_id: imdbId, rating: Number(rating) };
        const url = `${NAMEPSACE}rating/kino`;

        setSavingByImdbId(prev => ({ ...prev, [imdbId]: true }));
        setRatingOverrides(prev => ({ ...prev, [imdbId]: rating }));

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
            await res.json();
        } catch (err) {
            // Revert optimistic update for this row if save fails.
            setRatingOverrides(prev => {
                const next = { ...prev };
                delete next[imdbId];
                return next;
            });
            // eslint-disable-next-line no-console
            console.error('Failed to save rating from table:', err);
        } finally {
            setSavingByImdbId(prev => ({ ...prev, [imdbId]: false }));
        }
    }, []);

    if (!isBrowser) return null;

    return (
        <div className="movie-table-overlay" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <div className="movie-table-container" onMouseDown={(e) => e.stopPropagation()}>
                <table className="movie-table movie-table-fixed-head">
                    <thead>
                        <tr>
                            {columns.map(col => {
                                const active = orderBy === col.key;
                                const dir = active ? orderDir : undefined;
                                return (
                                    <th key={col.key} onClick={(e) => { e.stopPropagation(); onHeaderClick(col.key); }} onMouseDown={(e) => e.stopPropagation()} aria-sort={(col.sortable === false || orderBy !== col.key) ? 'none' : (orderDir === 'asc' ? 'ascending' : 'descending')}>
                                        <span>{col.label}</span>
                                        <span className={`sort-indicator ${active ? 'active' : ''}`}>
                                            {col.sortable === false ? '' : (active ? (dir === 'asc' ? '▲' : '▼') : '')}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                </table>
                <div ref={containerRef} className="movie-table-body" onScroll={onScroll}>
                    <table className="movie-table">
                        <tbody>
                            {rows.map((r, idx) => {
                                const effectiveRating = ratingOverrides[r.imdb_id] ?? r.kino_rating ?? 0;
                                const isSaving = !!savingByImdbId[r.imdb_id];

                                return (
                                <tr key={`${r.title}-${r.released || ''}-${idx}`} onClick={() => getSpecificMovie && r.imdb_id && getSpecificMovie(r.imdb_id)} style={{cursor: 'pointer'}}>
                                    <td>{r.title}</td>
                                    <td>{r.orig_language}</td>
                                    <td>{r.released ? String(r.released).slice(0, 10) : ''}</td>
                                    <td>{r.runtime}</td>
                                    <td>{r.imdb_rating ?? ''}</td>
                                    <td>{r.rotten_tomatoes_rating ?? ''}</td>
                                    {CAN_RATE ? <td className="table-rate-cell" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                                        <div className="table-rate-stars">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <button
                                                    key={`${r.imdb_id || idx}-${star}`}
                                                    type="button"
                                                    className="table-rate-star-btn"
                                                    disabled={isSaving || !r.imdb_id}
                                                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (!r.imdb_id) return;
                                                        saveRating(r.imdb_id, star);
                                                    }}
                                                    aria-label={`Rate ${r.title} ${star} stars`}
                                                >
                                                    <span className="table-rate-star-icon">{effectiveRating >= star ? '★' : '☆'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </td> : null}
                                </tr>
                            )})}
                            {rows.length === 0 && !isLoading && (
                                <tr><td className="empty" colSpan={columns.length}>No results</td></tr>
                            )}
                        </tbody>
                    </table>
                    {(isLoading || hasMore) && (
                        <div className="movie-table-loading">
                            {isLoading ? 'Loading…' : 'Scroll to load more…'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


